import { z } from 'zod';
import { Button } from './Button';
import { Switch } from './Checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog';
import { Label, ReadOnlyField } from './Field';
import { Form, FormField, type ZodForm } from './Form';
import { Input } from './Input';
import { Inline, Stack } from './Layout';
import { Select, type SelectOption } from './Select';
import { Text } from './Text';

export interface EditUserRoleField {
  /** The office roles the editor may give. */
  options: SelectOption[];
  value?: string;
  onChange: (role: string) => void;
  disabled?: boolean;
  /**
   * The menu's open state, when the container needs to close it before the
   * dialog unmounts. A Radix select left open inside a closing dialog keeps
   * its overlay on the page.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export interface EditUserDrivesField {
  /** Drives for the company: in driver lists, with the Driver permissions. */
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

/**
 * `canRename` rides in the form so the name is required only when it is
 * shown: a blank stored name must not block a role or Drives save by
 * someone who cannot change it.
 */
export const editUserSchema = z
  .object({ canRename: z.boolean(), name: z.string().trim() })
  .superRefine((values, ctx) => {
    if (values.canRename && !values.name) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['name'],
        message: 'Name is required.',
      });
    }
  });

export type EditUserValues = z.infer<typeof editUserSchema>;
export type EditUserForm = ZodForm<typeof editUserSchema>;

export interface EditUserDialogViewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * From `useZodForm(editUserSchema, …)`: the member's name, and whether the
   * viewer may rename them. Otherwise the name is read-only.
   */
  form: EditUserForm;
  /** Stays read-only for now; shown under the name. */
  email: string;
  /** Omit when the viewer may not change this member's office role. */
  role?: EditUserRoleField;
  /** Omit unless the member has driver details. */
  drives?: EditUserDrivesField;
  /** Instead of the switch, for a member with no driver details yet. */
  drivesHint?: string;
  /** Something is loading or a write is in flight: submit locks. */
  canSubmit?: boolean;
  /** A write is in flight: the submit relabels. */
  updating?: boolean;
  /** A write is in flight: cancel locks too. */
  cancelDisabled?: boolean;
  /** Receives the name the schema accepted, already trimmed. */
  onSubmit: (values: EditUserValues) => void;
}

/**
 * Edit a team member's settings: their name, their office role, and
 * whether they drive. Email is shown but not editable. Driving is its own
 * switch, never a role.
 *
 *   const form = useZodForm(editUserSchema, {
 *     defaultValues: { canRename: true, name },
 *   });
 *   <EditUserDialogView open={open} onOpenChange={setOpen}
 *     form={form} email="dana@…"
 *     role={{ options: roles, value: role, onChange: setRole }}
 *     drives={{ checked: drives, onChange: setDrives }}
 *     onSubmit={save} />
 *
 * The footer sits outside the form so DialogContent can hoist it clear of
 * the scrolling body; the submit button reaches the form by id.
 */
export function EditUserDialogView({
  open,
  onOpenChange,
  form,
  email,
  role,
  drives,
  drivesHint,
  canSubmit = true,
  updating = false,
  cancelDisabled = false,
  onSubmit,
}: EditUserDialogViewProps) {
  const canRename = form.watch('canRename');
  const body = (
    <>
      {canRename ? (
        <Stack gap={1}>
          <FormField
            form={form}
            name="name"
            label="Name"
            required
            disabled={updating}
          >
            {(field) => (
              <Input
                {...field}
                placeholder="Enter full name"
                autoComplete="off"
              />
            )}
          </FormField>
          <Text variant="caption" tone="muted">
            {email}
          </Text>
        </Stack>
      ) : (
        <ReadOnlyField label="User">
          <Stack gap={0}>
            <Text variant="body-sm-strong">{form.watch('name')}</Text>
            <Text variant="caption" tone="muted">
              {email}
            </Text>
          </Stack>
        </ReadOnlyField>
      )}

      {role && (
        <Stack gap={2}>
          <Label htmlFor="role">Office role</Label>
          <Select
            id="role"
            options={role.options}
            value={role.value}
            onValueChange={role.onChange}
            open={role.open}
            onOpenChange={role.onOpenChange}
            disabled={role.disabled}
            placeholder="Select a role"
          />
          <Text variant="body-sm" tone="muted">
            What they can do in the office. Driving is set separately below.
          </Text>
        </Stack>
      )}

      {!drives && drivesHint && (
        <Text variant="body-sm" tone="muted">
          {drivesHint}
        </Text>
      )}

      {drives && (
        <Stack gap={2}>
          <Inline gap={0} align="center" justify="between">
            <Label htmlFor="drives">Drives</Label>
            <Switch
              id="drives"
              checked={drives.checked}
              onCheckedChange={drives.onChange}
              disabled={drives.disabled}
            />
          </Inline>
          <Text variant="body-sm" tone="muted">
            {drives.checked
              ? 'Appears in driver lists and gets the Driver permissions.'
              : 'Not offered as a driver.'}
          </Text>
        </Stack>
      )}
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Edit User Settings</DialogTitle>
        </DialogHeader>
        <Form form={form} gap={4} id="edit-user-form" onSubmit={onSubmit}>
          {body}
        </Form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={cancelDisabled}
          >
            Cancel
          </Button>
          <Button form="edit-user-form" type="submit" disabled={!canSubmit}>
            {updating ? 'Updating...' : 'Update Settings'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
